<script setup lang="ts">
import { computed, ref, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import { useGetDiagnosticDrawings } from "@/stores/queries/drawings.query";
import { useDrawingStore } from "@/stores/fileStore";
import { useDeviceState } from "@/stores/DeviceState";
import DrawingItem from "@/components/files/DrawingList/DrawingItem.vue";
import LinkProjectDrawingDialog from "./LinkProjectDrawingDialog.vue";
import SectionPlaceholderAction from "./SectionPlaceholderAction.vue";

/**
 * Plans rattachés à l'ouvrage. Le lien est porté par `Drawing.diagnosticId` :
 * il se crée depuis l'outil dessin, ou ici en important un .dwg / en piochant
 * dans les plans déjà présents dans le projet.
 */

const props = defineProps<{
  diagnosticId: number;
  projectId: number;
}>();

const { t } = useTranslation();
const drawingStore = useDrawingStore();
const device = useDeviceState();

const { data: drawings, isLoading } = useGetDiagnosticDrawings(
  toRef(props, "diagnosticId")
);

const count = computed(() => drawings.value?.length ?? 0);

const showLinkDialog = ref(false);

// Import direct : le plan est créé dans le projet puis rattaché à l'ouvrage.
const fileInput = ref<HTMLInputElement | null>(null);

const openFileDialog = () => fileInput.value?.click();

const uploadFiles = (event: Event) => {
  const files = (event.target as HTMLInputElement).files;
  if (!files?.length) return;
  drawingStore.uploadFiles(Array.from(files), props.projectId, {
    diagnosticId: props.diagnosticId,
  });
  (event.target as HTMLInputElement).value = "";
};
</script>

<template>
  <section class="diagnostic-block">
    <header class="block-header">
      <h3 class="block-title">
        {{ t("report:diagnostics.plansTitle") }}
        <span v-if="count" class="count-pill">{{ count }}</span>
      </h3>
      <p class="block-description">
        {{ t("report:diagnostics.plansDescription") }}
      </p>
    </header>

    <div v-if="isLoading" class="text-center py-3">
      <span class="spinner-border spinner-border-sm" />
    </div>

    <template v-else>
      <p v-if="!count" class="empty-state mb-0">
        {{ t("report:diagnostics.plansEmpty") }}
      </p>

      <div v-else class="plans-list">
        <!-- La liste est déjà filtrée sur l'ouvrage : le rappel serait redondant -->
        <DrawingItem
          v-for="drawing in drawings"
          :key="drawing.id"
          :drawing="drawing"
          display="line"
          hide-diagnostic
        />
      </div>

      <!-- Les deux façons d'ajouter un plan, en prolongement de la liste -->
      <div class="placeholder-actions">
        <!-- Les deux actions écrivent côté serveur : inutilisables hors ligne -->
        <SectionPlaceholderAction
          icon="bi-link-45deg"
          :label="t('report:diagnostics.linkExistingPlan')"
          :disabled="device.isOnline === false"
          @click="showLinkDialog = true"
        />
        <SectionPlaceholderAction
          icon="bi-upload"
          :label="t('import.plan.dwg')"
          :disabled="device.isOnline === false"
          @click="openFileDialog"
        />
      </div>
    </template>

    <input
      ref="fileInput"
      type="file"
      class="d-none"
      multiple
      accept=".dwg"
      @change.prevent="uploadFiles"
    />

    <LinkProjectDrawingDialog
      v-model="showLinkDialog"
      :diagnostic-id="diagnosticId"
      :project-id="projectId"
    />
  </section>
</template>

<style lang="scss" scoped>
.diagnostic-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.block-header {
  margin: 0;
}

.block-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 600;
  margin: 0;
}

.count-pill {
  font-size: 12px;
  font-weight: 500;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  border-radius: 10px;
  padding: 1px 8px;
}

.block-description {
  font-size: 13px;
  color: var(--bs-secondary-color);
  margin: 4px 0 0;
}

.empty-state {
  color: var(--bs-secondary-color);
  font-size: 13px;
}

.plans-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.placeholder-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
